import type { MaterialCarbonBreakdown } from "@/integrations/agreements/client";

/** A partir de quantos itens a grade quebra em duas linhas (Figma node 345:16137). */
export const TWO_ROW_THRESHOLD = 4;
/** Itens na primeira linha quando há quebra. */
export const FIRST_ROW_SIZE = 3;
/** Piso de largura por bloco, em %, para o nome do resíduo não sumir. */
export const MIN_WIDTH_PERCENT = 12;

export interface CarbonBlock extends MaterialCarbonBreakdown {
  /** Largura do bloco dentro da sua linha, em porcentagem. */
  widthPercent: number;
}

export interface CarbonTreemapLayout {
  rows: CarbonBlock[][];
}

/**
 * True quando nenhum resíduo tem carbono positivo — mesmo filtro de
 * `buildCarbonTreemapLayout`, exposto à parte pra decidir se o card inteiro
 * aparece no Dashboard, não só o conteúdo do grid.
 */
export function isCarbonByMaterialEmpty(
  entries: MaterialCarbonBreakdown[],
): boolean {
  return !entries.some(
    (entry) => Number.isFinite(entry.carbon_saved) && entry.carbon_saved > 0,
  );
}

/**
 * Distribui as larguras de uma linha proporcionalmente ao carbono, com piso.
 *
 * O piso existe porque o bloco precisa caber o nome do resíduo: sem ele, um
 * material com 1% do total viraria uma tira de poucos pixels. O excedente
 * consumido pelo piso é descontado proporcionalmente dos blocos que estão
 * acima dele, então a soma continua 100%.
 */
export function distributeWidths(values: number[]): number[] {
  if (!values.length) return [];

  const safe = values.map((v) => (Number.isFinite(v) && v > 0 ? v : 0));
  const total = safe.reduce((sum, v) => sum + v, 0);

  // Sem nenhum valor positivo, divide igualmente em vez de dividir por zero.
  if (total <= 0) return safe.map(() => 100 / safe.length);

  const raw = safe.map((v) => (v / total) * 100);
  const floor = Math.min(MIN_WIDTH_PERCENT, 100 / safe.length);

  const belowFloor = raw.filter((w) => w < floor);
  if (!belowFloor.length) return raw;

  const deficit = belowFloor.reduce((sum, w) => sum + (floor - w), 0);
  const aboveTotal = raw
    .filter((w) => w >= floor)
    .reduce((sum, w) => sum + w, 0);

  return raw.map((w) =>
    w < floor ? floor : w - (aboveTotal > 0 ? (w / aboveTotal) * deficit : 0),
  );
}

/**
 * Organiza os blocos em linhas e calcula a largura de cada um.
 *
 * Até 3 itens fica em linha única — a quebra do Figma pressupõe pelo menos 4,
 * e forçá-la com menos deixaria a segunda linha órfã. A partir daí, 3 em cima
 * e o resto embaixo, como no desenho.
 */
export function buildCarbonTreemapLayout(
  entries: MaterialCarbonBreakdown[],
): CarbonTreemapLayout {
  const positive = entries.filter(
    (entry) => Number.isFinite(entry.carbon_saved) && entry.carbon_saved > 0,
  );
  if (!positive.length) return { rows: [] };

  const groups =
    positive.length < TWO_ROW_THRESHOLD
      ? [positive]
      : [positive.slice(0, FIRST_ROW_SIZE), positive.slice(FIRST_ROW_SIZE)];

  return {
    rows: groups
      .filter((group) => group.length > 0)
      .map((group) => {
        const widths = distributeWidths(group.map((e) => e.carbon_saved));
        return group.map((entry, index) => ({
          ...entry,
          widthPercent: widths[index],
        }));
      }),
  };
}
